import { useEffect, useMemo, useRef, useState } from "react";
import { Renderer } from "../../../engine/src/renderer.ts";
import { measureCard } from "../../../engine/src/textures.ts";
import type { DrawableElement } from "../../../engine/src/types.ts";
import { t } from "../lib/i18n.ts";
import { hitTest, projectAll, bounds, type Projected } from "../lib/overlay.ts";
import { addElement, newElement, resolvedValue, sceneAt, setPropertyAtTime } from "../lib/ops.ts";
import type { EditorStore } from "../lib/store.ts";
import { useApp } from "./common.tsx";
import { useStore } from "./hooks.ts";

type Tool = "move" | "rot3d" | "orbit";
type Drag = { kind: "move" | "scale" | "rotate" | "rot3d" | "orbit"; id: string; n: number; p0: { x: number; y: number }; start: Record<string, number>; ppuX: number; ppuY: number;
  center: { x: number; y: number }; a0: number; d0: number; mesh: boolean };
let dragCounter = 0;

export function Stage({ store }: { store: EditorStore }) {
  const st = useStore(store);
  const { api } = useApp();
  const p = st.project!;
  const wrap = useRef(null as HTMLDivElement | null), canvas = useRef(null as HTMLCanvasElement | null), svg = useRef(null as SVGSVGElement | null);
  const rend = useRef(null as Renderer | null), drag = useRef(null as Drag | null);
  const [tool, setTool] = useState("move" as Tool);
  const [box, setBox] = useState({ w: 0, h: 0 }); const [gpuErr, setGpuErr] = useState(false); const [fontTick, setFontTick] = useState(0);

  useEffect(() => {
    const el = wrap.current!, ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el); setBox({ w: el.clientWidth, h: el.clientHeight });
    void document.fonts?.ready.then(() => setFontTick((n: number) => n + 1));
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    try {
      const r = new Renderer(canvas.current!, p.width, p.height);
      r.setAssetResolver((id: string) => api.assetUrl(id)); r.onAssetReady = () => setFontTick((n: number) => n + 1);
      rend.current = r; setGpuErr(false);
    } catch { setGpuErr(true); }
    return () => { rend.current = null; };
  }, [p.width, p.height]);
  useEffect(() => {
    const r = rend.current; if (!r) return;
    // draw synchronously: deferring to requestAnimationFrame and cancelling on cleanup starved every frame during playback
    const dpr = Math.min(1.5, window.devicePixelRatio || 1);
    const fit = Math.min(1, Math.max(0.15, (box.w - 56) / p.width), Math.max(0.15, (box.h - 56) / p.height));
    r.setOutputSize(p.width * fit * dpr, p.height * fit * dpr);
    try { r.renderFrame(p, st.timeMs); } catch { /* keep last frame */ }
  }, [p, st.timeMs, box.w, box.h, fontTick, gpuErr]);

  useEffect(() => {
    const r = rend.current; if (!r) return;
    r.setPlayback(st.playing, p, st.timeMs);
  }, [st.playing]);

  const pad = 28, aw = Math.max(50, box.w - pad * 2), ah = Math.max(50, box.h - pad * 2);
  const scale = Math.min(aw / p.width, ah / p.height), fw = Math.floor(p.width * scale), fh = Math.floor(p.height * scale);
  const items = useMemo(() => projectAll(p, st.timeMs, (e: DrawableElement) => e.type === "mesh" ? { width: e.size, height: e.size } : measureCard(e)), [p, st.timeMs, fontTick]);
  const sel = st.selection.kind === "element" ? items.find((i: Projected) => i.id === (st.selection as { id: string }).id) : undefined;
  const k = 1 / scale; // project px per screen px

  const toProject = (e: { clientX: number; clientY: number }) => {
    const r = svg.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width * p.width, y: (e.clientY - r.top) / r.height * p.height };
  };
  const startDrag = (kind: Drag["kind"], id: string, pt: { x: number; y: number }) => {
    const it = items.find((i: Projected) => i.id === id), el = p.elements[id]; if (!it || !el) return;
    const rv = (prop: any) => resolvedValue(p, id, prop, st.timeMs);
    drag.current = { kind, id, n: ++dragCounter, p0: pt, ppuX: it.pxPerUnitX, ppuY: it.pxPerUnitY, center: it.center, mesh: el.type === "mesh",
      start: { x: rv("x"), y: rv("y"), scale_x: rv("scale_x"), scale_y: rv("scale_y"), scale_z: rv("scale_z"), rotation: rv("rotation") },
      a0: Math.atan2(pt.y - it.center.y, pt.x - it.center.x), d0: Math.max(4, Math.hypot(pt.x - it.center.x, pt.y - it.center.y)) };
  };
  const cameraId = () => { const sc = sceneAt(p, st.timeMs); return sc?.element_ids.find((id) => p.elements[id]?.type === "camera"); };
  const pickTool = (tl: Tool) => {
    if (tl === "orbit" && !cameraId()) {                                // orbiting needs a camera: create it (undoable) rather than pretend
      const sc = sceneAt(p, st.timeMs); if (!sc) return;
      const el = newElement("camera", p); store.edit((q) => addElement(q, sc.id, el));
    }
    setTool(tl);
  };
  const onWheel = (e: any) => {
    const cid = cameraId(); if (tool !== "orbit" || !cid) return;
    const d = resolvedValue(p, cid, "distance", st.timeMs) || 1;
    store.edit((q) => setPropertyAtTime(q, cid, "distance", Math.min(10, Math.max(0.05, d * (e.deltaY > 0 ? 1.08 : 0.92))), st.timeMs), `wheel:${cid}`);
  };
  const onDown = (e: any) => {
    if (e.button !== 0 || st.playing && false) return;
    if (tool === "orbit") {
      const cid = cameraId(); if (!cid) return;
      const time = st.timeMs, rv = (prop: any) => resolvedValue(p, cid, prop, time);
      drag.current = { kind: "orbit", id: cid, n: ++dragCounter, p0: toProject(e), ppuX: 1, ppuY: 1, center: { x: 0, y: 0 }, a0: 0, d0: 1, mesh: false,
        start: { yaw: rv("rotation_y"), pitch: rv("rotation_x") } };
      store.select({ kind: "element", id: cid }); svg.current!.setPointerCapture(e.pointerId); return;
    }
    const pt = toProject(e), h = (e.target as SVGElement).dataset?.h as Drag["kind"] | undefined;
    if (h && sel) { startDrag(h, sel.id, pt); svg.current!.setPointerCapture(e.pointerId); return; }
    const id = hitTest(items, pt);
    if (id) {
      store.select({ kind: "element", id });
      if (tool === "rot3d" && p.elements[id]?.type !== "camera") {
        const rv = (prop: any) => resolvedValue(p, id, prop, st.timeMs);
        drag.current = { kind: "rot3d", id, n: ++dragCounter, p0: pt, ppuX: 1, ppuY: 1, center: { x: 0, y: 0 }, a0: 0, d0: 1, mesh: false, start: { yaw: rv("rotation_y"), pitch: rv("rotation_x") } };
      } else startDrag("move", id, pt); svg.current!.setPointerCapture(e.pointerId); }
    else store.select({ kind: "none" });
  };
  const onMove = (e: any) => {
    const d = drag.current; if (!d) return;
    const pt = toProject(e), time = store.getState().timeMs, key = `drag:${d.id}:${d.n}`;
    if (d.kind === "rot3d" || d.kind === "orbit") {
      const sgn = d.kind === "orbit" ? -1 : 1;                           // dragging the scene right turns the camera the other way round the subject
      const yaw = d.start.yaw! + sgn * (pt.x - d.p0.x) * 0.1, pitch = Math.max(-89, Math.min(89, d.start.pitch! - sgn * (pt.y - d.p0.y) * 0.1));
      store.edit((q) => setPropertyAtTime(setPropertyAtTime(q, d.id, "rotation_y", yaw, time), d.id, "rotation_x", pitch, time), key);
    } else if (d.kind === "move") {
      const x = d.start.x! + (pt.x - d.p0.x) / d.ppuX, y = d.start.y! + (pt.y - d.p0.y) / d.ppuY;
      store.edit((q) => setPropertyAtTime(setPropertyAtTime(q, d.id, "x", x, time), d.id, "y", y, time), key);
    } else if (d.kind === "scale") {
      const f = Math.max(0.02, Math.hypot(pt.x - d.center.x, pt.y - d.center.y) / d.d0);
      store.edit((q) => {
        let n = setPropertyAtTime(q, d.id, "scale_x", d.start.scale_x! * f, time);
        n = setPropertyAtTime(n, d.id, "scale_y", d.start.scale_y! * f, time);
        return d.mesh ? setPropertyAtTime(n, d.id, "scale_z", d.start.scale_z! * f, time) : n;
      }, key);
    } else {
      const da = (Math.atan2(pt.y - d.center.y, pt.x - d.center.x) - d.a0) * 180 / Math.PI;
      store.edit((q) => setPropertyAtTime(q, d.id, "rotation", d.start.rotation! + da, time), key);
    }
  };
  const onUp = () => { drag.current = null; };

  const hb = sel ? bounds(sel.hull) : null;
  const r = 6 * k;
  return (
    <div className="stage" ref={wrap}>
      <div className="stage-tools" role="group" aria-label={t("st3.tools")}>
        {(["move", "rot3d", "orbit"] as const).map((tl) => <button key={tl} className="btn sm" aria-pressed={tool === tl} title={t(`st3.${tl}_h` as any)} onClick={() => pickTool(tl)}>{t(`st3.${tl}` as any)}</button>)}
      </div>
      <div className="frame" style={{ width: fw, height: fh }}>
        <canvas ref={canvas} aria-label="Canvas" />
        {gpuErr && <div className="empty" style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }} role="alert">WebGL2 indisponible / unavailable</div>}
        <svg ref={svg} viewBox={`0 0 ${p.width} ${p.height}`} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onWheel={onWheel} data-testid="overlay" style={{ cursor: tool === "orbit" ? "move" : undefined, touchAction: "none" }}>
          {sel && hb && (<g>
            <polygon className="sel-outline" points={sel.hull.map((q: { x: number; y: number }) => `${q.x},${q.y}`).join(" ")} />
            <line x1={(hb.minX + hb.maxX) / 2} y1={hb.minY} x2={(hb.minX + hb.maxX) / 2} y2={hb.minY - 22 * k} className="sel-outline" />
            <circle className="handle" data-h="rotate" cx={(hb.minX + hb.maxX) / 2} cy={hb.minY - 22 * k} r={r} style={{ cursor: "grab" }} />
            <rect className="handle" data-h="scale" x={hb.maxX - r} y={hb.maxY - r} width={r * 2} height={r * 2} style={{ cursor: "nwse-resize" }} />
          </g>)}
        </svg>
      </div>
      {p.scenes.length === 0 && <div className="stagehint">{t("lib.no_scene")}</div>}
    </div>
  );
}
